import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import dayjs from 'dayjs';
import { useShallow } from 'zustand/react/shallow';

import { Checkbox, Dialog, Text, toastService } from '@thefarmersfront/kitchen-mobile';

import { DIALOG_MESSAGES, DIALOG_TITLES, TOAST_MESSAGES } from '~/constants';
import { WEB_STORAGE_TYPE, webStorageService } from '~/services';
import { useShouldCheckModalStore, useUserInfoStore, useWorkInfoStore } from '~/store';

import styles from './ChangePasswordModal.module.scss';

/**
 * ChangePasswordModal 컴포넌트 props type 정의
 * @category ChangePasswordModal
 */
export type TypeChangePasswordModalProps = {
  /** 로그인 ID */
  loginId?: string;
  /** 비밀번호 */
  password?: string;
};

/**
 * @description 비밀번호 변경 안내 모달 컴포넌트
 * @returns - React.JSX.Element / ChangePasswordModal 컴포넌트 반환
 */
const ChangePasswordModal = ({ loginId, password }: TypeChangePasswordModalProps): React.JSX.Element => {
  const [isHiddenFor30Day, setIsHiddenFor30Day] = useState(false);

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const {
    changePasswordModal: { open, type, message, onNextTime },
    setChangePasswordModal,
  } = useShouldCheckModalStore(
    useShallow((state) => ({
      changePasswordModal: state.changePasswordModal,
      setChangePasswordModal: state.actions.setChangePasswordModal,
    }))
  );

  const { passwordChangeRequired, initPassword, getIsContractTypeShort, getIsPasswordChangeTarget } = useUserInfoStore(
    useShallow((state) => ({
      passwordChangeRequired: state.passwordChangeRequired,
      initPassword: state.initPassword,
      getIsContractTypeShort: state.actions.getIsContractTypeShort,
      getIsPasswordChangeTarget: state.actions.getIsPasswordChangeTarget,
    }))
  );
  const getIsContractable = useWorkInfoStore((state) => state.actions.getIsContractable);

  useEffect(() => {
    const isEntryPath = ['/', '/main'];

    if (isEntryPath.includes(pathname)) {
      return;
    }

    const { open: isPasswordChangeTarget, type: passwordChangeTargetType } = getIsPasswordChangeTarget();
    const reConsentRequired = useUserInfoStore.getState().reConsentRequired;

    if (isPasswordChangeTarget && !reConsentRequired) {
      setChangePasswordModal({
        open: true,
        type: passwordChangeTargetType,
        message:
          passwordChangeTargetType === 'initPassword'
            ? DIALOG_MESSAGES.SHOULD_CHANGE_PASSWORD
            : DIALOG_MESSAGES.NEED_TO_CHANGE_PASSWORD,
      });
    }
  }, [pathname, passwordChangeRequired, initPassword]);

  const webStorage = webStorageService(WEB_STORAGE_TYPE.LOCAL_STORAGE);

  const handleChangePw = (): void => {
    setChangePasswordModal({ open: false, message: '', isChecked: true });
    navigate('/change-pw', { state: { loginId: loginId, currentPassword: password } });
  };

  const handleChangeNextTime = async (): Promise<void> => {
    if (isHiddenFor30Day) {
      toastService.addToast({
        message: TOAST_MESSAGES.CHANGE_PASSWORD_NEXT_TIME,
      });
      webStorage.setSkipChangePasswordDate(dayjs());
    }

    setChangePasswordModal({ open: false, message: '', isChecked: true, onNextTime: undefined });

    // 로그인 페이지에서 다음에 변경하기 버튼 클릭 시 : 네트워크 체크 페이지 또는 출근 페이지로 이동
    if (pathname === '/login') {
      await onNextTime?.();
      if (getIsContractTypeShort() && getIsContractable()) {
        navigate('/check-network');
      } else {
        navigate('/start-work');
      }
    }
  };

  const handleCheckBoxChange = (): void => {
    setIsHiddenFor30Day(!isHiddenFor30Day);
  };

  if (type === 'over90Days') {
    return (
      <Dialog
        open={open}
        title={DIALOG_TITLES.NEED_TO_CHANGE_PASSWORD}
        type="confirm"
        onConfirm={handleChangePw}
        onCancel={handleChangeNextTime}
        confirmText="지금 변경"
        cancelText="다음에"
      >
        <Text color="secondary">{message}</Text>
        <Checkbox
          className={styles['checkbox']}
          label="30일간 다시 보지 않기"
          checked={isHiddenFor30Day}
          onChange={handleCheckBoxChange}
        />
      </Dialog>
    );
  }

  return (
    <Dialog open={open} type="alert" onConfirm={handleChangePw} confirmText="지금 변경">
      <Text color="secondary">{message}</Text>
    </Dialog>
  );
};

export default ChangePasswordModal;
